import { AutoHeight, Button, DialogV2 } from '@recruit-robin/robin-theme/web-components';
import { ChevronLeft } from '@recruit-robin/robin-theme/web-icons';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';

import { useGetJobImproved } from '@/entities/job';
import { useDictionary } from '@/shared/lib/i18n';
import { PublicVacancyResponse } from 'clients/CampaignsClient/CampaignsClient.types';
import { VacancyDetails } from 'components/ui/organisms/VacancyDetails/VacancyDetails';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { useStyles } from './CandidatePreviewModal.styles';
import type { CandidatePreviewModalProps } from './CandidatePreviewModal.types';
import { MessagePreview } from './message-preview';

const ANIMATION_DURATION = 0.23;

export const CandidatePreviewModal = ({
  isVisible,
  onClose,
  message,
  jobImprovedId,
  previewVacancy,
  isIndeedFlow = false,
}: CandidatePreviewModalProps) => {
  const [step, setStep] = useState<'message' | 'job'>('message');
  const classes = useStyles();
  const dictionary = useDictionary();

  const handleBack = () => {
    if (step === 'job') {
      setStep('message');
      return;
    }

    onClose();
  };

  return (
    <DialogV2.Root customCss={classes.dialog} isVisible={isVisible} variant="from-click">
      <DialogV2.Backdrop />
      <DialogV2.Content customCss={classes.content}>
        <DialogV2.Body customCss={classes.body}>
          <div css={classes.bodyHeader}>
            <Button
              label={dictionary.backToEditor}
              onClick={handleBack}
              style="text"
              type="subtle2"
              startIcon={ChevronLeft}
            />
          </div>

          <AutoHeight duration={ANIMATION_DURATION} ease="easeOut">
            <AnimatePresence mode="popLayout" initial={false}>
              {step === 'message' && (
                <motion.div
                  key="message"
                  css={classes.stepContainer}
                  initial={{ x: '-100%', opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: '-100%', opacity: 0 }}
                  transition={{ duration: ANIMATION_DURATION }}>
                  <MessagePreview
                    message={message}
                    jobImprovedId={jobImprovedId}
                    onViewJob={() => setStep('job')}
                    jobTitle={previewVacancy?.job.job_details.job_title}
                    isIndeedFlow={isIndeedFlow}
                  />
                </motion.div>
              )}
              {step === 'job' && (jobImprovedId || previewVacancy) && (
                <motion.div
                  key="job"
                  css={classes.stepContainer}
                  initial={{ x: '100%', opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: '100%', opacity: 0 }}
                  transition={{ duration: ANIMATION_DURATION }}>
                  <_JobDetails jobImprovedId={jobImprovedId} previewVacancy={previewVacancy} />
                </motion.div>
              )}
            </AnimatePresence>
          </AutoHeight>
        </DialogV2.Body>
      </DialogV2.Content>
    </DialogV2.Root>
  );
};

const _JobDetails = ({
  jobImprovedId,
  previewVacancy,
}: {
  jobImprovedId: number;
  previewVacancy?: PublicVacancyResponse;
}) => {
  const { data: currentUser } = useCurrentUserQuery();
  const { data: jobImproved } = useGetJobImproved(jobImprovedId);

  const publicVacancy = useMemo(() => {
    if (previewVacancy) return previewVacancy;
    if (!jobImproved) return null;
    const tenantName = currentUser?.tenant?.name ? { tenant_name: currentUser?.tenant?.name } : {};

    return {
      job: { ...jobImproved, ...tenantName, db_id: jobImprovedId },
      accept_token: '',
      reject_token: '',
    } as unknown as PublicVacancyResponse;
  }, [previewVacancy, jobImproved, currentUser?.tenant?.name, jobImprovedId]);

  return <VacancyDetails vacancy={publicVacancy} isLoading={false} error={null} onSubmit={() => {}} readonly />;
};
